import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from '../../../../axiosConfig';
import LatestDevelopments from './LatestDevelopments';
import EarningsPress from './EarningsPress';
import styles from '../MicroProbe.module.css';
import IdeasMaker from '../../IdeasMaker';
import SecFilings from './SecFillings';
import SentimentTrend from './SentimentTrend';
import Networks from './Networks';
import ChatNumbers from './ChatNumbers';
import FinancialsOverview from './FinancialsOverview';
import FinancialData from './FinancialData';
import StockPerformanceOverview from './StockPerformanceOverview';
import SignalsBacktestTechnicalsPlot from './SignalsBacktestTechnicalsPlot';
import SignalsBacktestTechnicals from './SignalsBacktestTechnicals';
import SignalsBacktestFundamentals from './SignalsBacktestFundamentals';
import SignalsBacktestAI from './SignalsBacktestAI';
import SignalsBacktestMacro from './SignalsBacktestMacro';
import VideoInsight from './VideoInsightForDashboard';
import DocumentUploads from './MicroProbeDocuments';
import TopicGather from '../../TopicGather';

const Companies = () => {
  const { companyName } = useParams();
  const location = useLocation(); // Use useLocation hook to access the location object
  const [companyData, setCompanyData] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set()); // Track multiple expanded sections

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        let data;

        if (location.state && location.state.ticker) {
          const { ticker } = location.state;
          data = { name: companyName, symbol: ticker };
        } else {
          const response = await axios.get(`/api/company/name/${companyName}`);
          data = response.data;

        }

        setCompanyData(data);
     
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };
  
    fetchCompanyData();
  }, [companyName, location.state]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

return (
  <div className={styles.container}>
    <h1 className={styles.title}>Company Analysis: {companyName.includes('_ticker_') ? companyName.split('_ticker_')[0] : companyName}</h1>

    {companyData && (
      <>
        {/* Overview Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('overview')}
          >
            <h2>Latest Developments</h2>
            <span className={`${styles.arrow} ${expandedSections.has('overview') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('overview') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionBg1}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <LatestDevelopments  companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionQuarter} ${styles.sectionBg2}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SentimentTrend  companyData={companyData} />
              </div>
              
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <Networks  companyData={companyData} />
              </div>
            </div>
          </div>
        </div>

        {/* Performance Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('performance')}
          >
            <h2>Performance</h2>
            <span className={`${styles.arrow} ${expandedSections.has('performance') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('performance') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionQuarter} ${styles.sectionBg3}`}>
              <div className={styles.sectionContent}>
                <StockPerformanceOverview  companyData={companyData} />
              </div>
              <div className={styles.sectionContent}>
                <FinancialsOverview companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionBg3}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <FinancialData companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionBg3}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>     
                <ChatNumbers companyData={companyData} />
              </div>
            </div>
          </div>
        </div>

        {/* Company Statements Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('statements')}
          >
            <h2>Company Statements</h2>
            <span className={`${styles.arrow} ${expandedSections.has('statements') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('statements') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionBg4}`}>
              <div className={styles.sectionContent}>
                <EarningsPress companyData={companyData} />
              </div>
            </div>
            
            <div className={`${styles.section} ${styles.sectionBg4}`}>
              <div className={styles.sectionContent}>
                <SecFilings companyData={companyData} />
              </div>
            </div>
          </div>
        </div>

        {/* Risk and Signals Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('signals')}
          >
            <h2>Risks & Signals</h2>
            <span className={`${styles.arrow} ${expandedSections.has('signals') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('signals') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionBg5}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SignalsBacktestAI companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionQuarter} ${styles.sectionBg5}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SignalsBacktestTechnicals companyData={companyData} />
              </div>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SignalsBacktestTechnicalsPlot companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionBg5}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SignalsBacktestFundamentals companyData={companyData} />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionBg5}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>
                <SignalsBacktestMacro companyData={companyData} />
              </div>
            </div>
          </div>
        </div>


        {/* Documents and Media Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('documents')}
          >
            <h2>Documents & Media</h2>
            <span className={`${styles.arrow} ${expandedSections.has('documents') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('documents') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionBg4}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto'}}> 
                <TopicGather />
              </div>
            </div>

            <div className={`${styles.section} ${styles.sectionBg4}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto'}}> 
                <DocumentUploads />
              </div>
            </div>
            
            <div className={`${styles.section} ${styles.sectionBg4}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto'}}>
                <VideoInsight />
              </div>
            </div>
          </div>
        </div>


        {/* Report Section */}
        <div className={styles.collapsibleSection}>
          <button 
            className={styles.sectionHeader}
            onClick={() => toggleSection('report')}
          >
            <h2>Reports</h2>
            <span className={`${styles.arrow} ${expandedSections.has('report') ? styles.open : ''}`}>›</span>
          </button>
          <div style={{ display: expandedSections.has('report') ? 'block' : 'none' }}>
            <div className={`${styles.section} ${styles.sectionQuarter} ${styles.sectionBg6}`}>
              <div className={styles.sectionContent} style={{ width: '100%', height: 'auto' }}>   
                <IdeasMaker />
              </div>
            </div>
          </div>
        </div>
      </>
    )}

    <Link to="/entity-intelligence" style={{ marginTop: '2rem', display: 'inline-block' }}>Back</Link>
  </div>
);
};

export default Companies;